import {Component, ElementRef, Input, numberAttribute, OnInit, OnDestroy, ViewChild, ChangeDetectorRef} from '@angular/core';
import {QuestionService} from "../service/question-pool/question.service";
import {NavbarComponent} from "../navbar/navbar.component";
import { CommonModule, NgClass } from "@angular/common";
import {Router, RouterLink, RouterOutlet, ActivatedRoute, ParamMap, RouterStateSnapshot} from '@angular/router';
import {
  QuestionPoolQuestionDetailComponent
} from "../question-pool-question-detail/question-pool-question-detail.component";
import {DeviceDetectorService} from "ngx-device-detector";
import { Id2Question, RestService } from '../service/rest/rest.service';
import { AuthService } from "../service/auth/auth.service";
import { Question } from '../model/question';
import { KeyValuePipe } from '@angular/common';
import { LoggerService } from '../service/utils/logger.service';
import { FormsModule, FormArray } from '@angular/forms';
import { CanDeactivateTest } from '../service/auth/can-deactivate-test.guard';
import { Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { FlightComputerComponent, FlightComputerUiMode } from '../components/flight-computer/flight-computer.component';


@Component({
  selector: 'app-question-pool-study',
  standalone: true,
  imports: [
    CommonModule,
    NavbarComponent,
    RouterLink,
    RouterOutlet,
    QuestionPoolQuestionDetailComponent,
    NgClass,
    KeyValuePipe,
    FormsModule,
    FlightComputerComponent
  ],
  templateUrl: './question-pool-study.component.html',
  styleUrl: './question-pool-study.component.css'
})


export class QuestionPoolStudyComponent implements OnInit, OnDestroy, CanDeactivateTest {
  // protected questionIds: number[] = [];
  private _questionId: number = 1;
  private _poolId: string = '';
  protected mobile:boolean = false;
  protected questionsMap: Id2Question = { };

  // Tab management
  activeTab: 'question' | 'explanation' | 'note' | 'exam' | 'comments' = 'question';
  
  // Explanation tab data
  isLoadingExplanation = false;
  explanationData: {
    explanationHtml?: string;
    figureExplanation?: string;
    explanationImageUrls?: string[];
    hasE6bExplanation?: boolean;
    e6bInstructions?: unknown[];
    e6bTitle?: string | null;
  } | null = null;
  
  // Note tab data
  questionNotes: string = '';
  savedNotes: string = ''; // Track saved notes to detect changes
  hasNote: boolean = false;
  private notesSaveTimeout: any = null;
  
  // Exam state tab data
  isLoadingExamState = false;
  examStateData: { country: string; date: string }[] = [];
  
  // Comments tab data
  isLoadingComments = false;
  commentsData: { id: number; username: string; comment: string; createdAt: string; userId: number }[] = [];
  newComment: string = '';
  isSubmittingComment = false;
  showCommentConfirm = false;
  canSubmitComment = true;
  currentUserId: number | null = null;

  // Flag and hide state
  currentFlagId: number | null = null; // 1=Red, 2=Green, 3=Yellow
  currentHide: number = 0; // 0 or 1
  isLoadingState = false;
  
  // Shortcuts modal
  showShortcutsModal = false;

  // Flight computer overlay (interactive tools or explanation playback)
  showFlightComputer = false;
  flightComputerUiMode: FlightComputerUiMode = 'interactive';
  flightComputerInstructions: unknown[] | null = null;
  private flightComputerOpenedAt = 0;
  private flightComputerOpenTimer: ReturnType<typeof setTimeout> | null = null;

  // User test settings (shuffle answers, auto-advance on correct)
  userTestSettings: { shuffleQuestionAnswers: boolean; randomizeQuestions: boolean; nextAutomatically: boolean } | null = null;

  // Pagination for question grid when > 50 questions
  protected readonly QUESTIONS_PER_PAGE = 50;
  protected questionGridPage = 1;

  /** Mobile-only bottom-sheet for the question grid. The bar is sticky at the
   * bottom of the viewport on phones, so the question stays visible and
   * Cancel / Finish are always one tap away. Tapping "Q X/Y" opens this sheet. */
  protected gridSheetOpen = false;

  // Image overlay (click explanation image to view full size)
  imageOverlayUrl: string | null = null;

  // Store bound event handlers for proper cleanup
  private boundKeyboardHandler?: (event: KeyboardEvent) => void;
  private boundVisibilityHandler?: () => void;
  
  // Prevent rapid navigation - use a static variable that persists across component instances
  private static isNavigating = false;
  private static lastNavigatedQuestionId: number | null = null;
  private static keyboardListenerAdded = false;
  private static keyboardHandlerInstance: QuestionPoolStudyComponent | null = null;
  private static keyboardSeq = 0;

  // container html element reference
  @ViewChild('container') container: ElementRef | undefined;
  @ViewChild(QuestionPoolQuestionDetailComponent) questionDetail!: QuestionPoolQuestionDetailComponent;
  @ViewChild('leaveModalStayBtn') leaveModalStayBtn: ElementRef<HTMLButtonElement> | undefined;

  // question id getter and setter
  @Input({required: true, transform: numberAttribute})
  set questionId(value: number) {
    console.log('[DEBUG questionId setter] Setting questionId to:', value, 'old value:', this._questionId);
    // Save notes before changing question
    if (this._questionId !== value) {
      console.log('[DEBUG questionId setter] Value changed, auto-saving notes');
      this.autoSaveNotes();
    }
    this._questionId = value;
    // Reset to question tab when question changes
    this.activeTab = 'question';
    // When paginated, ensure current page contains this question
    this.ensurePageContainsQuestion(value);
    // Load tab data when question changes
    this.loadTabData();
    console.log('[DEBUG questionId setter] Done setting questionId');
  }
  get questionId(): number {
    return this._questionId;
  }

  // constructor
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private deviceService: DeviceDetectorService,
    protected restService: RestService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
  ) {
    this.mobile = this.deviceService.isMobile();
    //this.soapService = soapService;
  }

  // ngOnInit
  ngOnInit() {
    console.log('[DEBUG ngOnInit] Component initializing');
    // Store bound handlers for proper cleanup
    this.boundKeyboardHandler = this.handleKeyboardShortcuts.bind(this);
    
    // Add keyboard event listener for shortcuts - only once using static flag
    if (!QuestionPoolStudyComponent.keyboardListenerAdded) {
      // Use capture phase so we still receive events even if something stops propagation.
      window.addEventListener('keydown', this.boundKeyboardHandler, true);
      QuestionPoolStudyComponent.keyboardListenerAdded = true;
      QuestionPoolStudyComponent.keyboardHandlerInstance = this;
      console.log('[DEBUG ngOnInit] Event listeners registered');
    } else {
      // Update the instance reference so the handler uses the current component
      QuestionPoolStudyComponent.keyboardHandlerInstance = this;
      console.log('[DEBUG ngOnInit] Keyboard listener already exists, updated instance reference');
    }

    // Reload settings when tab/window becomes visible (e.g. user changed settings in another tab)
    this.boundVisibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        void this.loadUserTestSettings().then(() => this.cdr.markForCheck());
      }
    };
    document.addEventListener('visibilitychange', this.boundVisibilityHandler);

    // Load settings first so nextAutomatically is available before user can answer
    void this.loadUserTestSettings().then(() => {
      this.route.paramMap.subscribe((params: ParamMap) => {
        const newQuestionId = +params.get('questionId')!;
        console.log('[DEBUG ngOnInit] Route param changed, new questionId:', newQuestionId, 'old questionId:', this.questionId);
        this.questionId = newQuestionId;  // Extract the questionId from the route
        // Clear lastNavigatedQuestionId when route actually changes to prevent blocking
        if (QuestionPoolStudyComponent.lastNavigatedQuestionId === newQuestionId) {
          QuestionPoolStudyComponent.lastNavigatedQuestionId = null;
        }
        //this._questionId = this.questionId || 1;  // Default to 1 if questionId is not provided
        this.loadQuestions();
      });
    });
  }

  private async loadUserTestSettings() {
    const user = this.authService.getUser();
    const sessionId = user?.sid;
    if (sessionId) {
      this.restService.clearUserTestSettingsCache();
      this.userTestSettings = await this.restService.ensureUserTestSettingsLoaded(sessionId);
    }
  }

  ngOnDestroy() {
    console.log('[DEBUG ngOnDestroy] Component destroying, removing event listeners');
    if (this.flightComputerOpenTimer != null) {
      clearTimeout(this.flightComputerOpenTimer);
      this.flightComputerOpenTimer = null;
    }
    this.closeE6bPlayer();
    // Save notes before component is destroyed
    this.autoSaveNotes();
    if (this.boundVisibilityHandler) {
      document.removeEventListener('visibilitychange', this.boundVisibilityHandler);
    }
    // Clear instance reference if this is the active instance
    if (QuestionPoolStudyComponent.keyboardHandlerInstance === this) {
      // Check if we're navigating away from study pages
      const currentUrl = this.router.url;
      if (!currentUrl.includes('/question-pool-study/')) {
        // Remove keyboard listener if navigating away from study pages
        if (this.boundKeyboardHandler) {
          // Remove both capture and bubble registrations (defensive)
          window.removeEventListener('keydown', this.boundKeyboardHandler, true);
          window.removeEventListener('keydown', this.boundKeyboardHandler);
          QuestionPoolStudyComponent.keyboardListenerAdded = false;
          console.log('[DEBUG ngOnDestroy] Removed keyboard listener (navigating away from study)');
        }
        QuestionPoolStudyComponent.keyboardHandlerInstance = null;
      } else {
        // Keep listener active, just clear instance reference
        // The next component instance will set itself as the handler
        QuestionPoolStudyComponent.keyboardHandlerInstance = null;
        console.log('[DEBUG ngOnDestroy] Cleared instance reference (staying in study mode)');
      }
    }
    // Clear any pending timeout
    if (this.notesSaveTimeout) {
      clearTimeout(this.notesSaveTimeout);
    }
  }

  async loadQuestions() {
    let user = this.authService.getUser();
    const sessionId = user?.sid;
    if (sessionId != null) {
      let questionIds = this.restService.getQuestionIds();
      
      // CRITICAL: Only reload questionIds from backend if they're truly empty AND we have a test ID
      // AND questionIds haven't been loaded for this test yet (to prevent reloading during navigation)
      if (questionIds.length === 0 && this.restService.currentUserTestId && 
          this.restService.currentUserTestId !== this.restService.questionIdsLoadedForTest) {
        console.log('[loadQuestions] Study - questionIds empty, loading from user test:', this.restService.currentUserTestId);
        const resumeData = await this.restService.getTestForResume(sessionId, this.restService.currentUserTestId);
        if (resumeData && resumeData.questions) {
          // Backend returns questions ordered by questionOrder, which should match original test order
          const allQuestionIds = resumeData.questions.map(q => q.questionId);
          console.log('[loadQuestions] Study - Loaded questionIds from backend (first 10):', allQuestionIds.slice(0, 10));
          this.restService.setQuestionIdsForResume(allQuestionIds);
          this.restService.setMaxQuestions(allQuestionIds.length);
          questionIds = this.restService.getQuestionIds();
          console.log('[loadQuestions] Study - Loaded', questionIds.length, 'questionIds from test');
        }
      } else if (questionIds.length > 0) {
        // QuestionIds already loaded - don't reload to preserve order
        console.log('[loadQuestions] Study - questionIds already loaded (', questionIds.length, 'questions), preserving order');
      } else if (this.restService.currentUserTestId === this.restService.questionIdsLoadedForTest) {
        console.log('[loadQuestions] Study - questionIds already loaded for this test, skipping reload');
      }
      
      // Update questionIds after potentially loading them
      questionIds = this.restService.getQuestionIds();
      this.questionsMap = this.restService.getQuestionsMap();
      
      // Ensure we're on a valid question from the test
      if (questionIds.length > 0) {
        const currentRouteId = this.route.snapshot.paramMap.get('questionId');
        const currentRouteIdNum = currentRouteId ? +currentRouteId : null;
        
        // Only navigate if current route questionId is not in the test's questionIds
        if (currentRouteIdNum && !questionIds.includes(currentRouteIdNum)) {
          const firstQuestionId = questionIds[0];
          console.log('[loadQuestions] Study - Current route questionId', currentRouteIdNum, 'not in test, navigating to first question:', firstQuestionId);
          // Navigate to the first question to avoid ghost questions
          this.router.navigate(['/question-pool-study/', firstQuestionId], { replaceUrl: true });
          return; // Exit early, navigation will trigger route change and reload
        }
        // Ensure the current question is loaded
        const questionIdToLoad = currentRouteIdNum && questionIds.includes(currentRouteIdNum) ? currentRouteIdNum : questionIds[0];
        await this.restService.getQuestion(questionIdToLoad, sessionId);
      }
    }
    
    // Load initial tab data
    this.loadTabData();
  }

  selectTab(tab: 'question' | 'explanation' | 'note' | 'exam' | 'comments') {
    // Save notes before switching tabs
    if (this.activeTab === 'note') {
      this.autoSaveNotes();
    }
    this.activeTab = tab;
    this.loadTabData();
  }

  async loadTabData() {
    const user = this.authService.getUser();
    const sessionId = user?.sid;
    if (!sessionId) {
      return;
    }

    // Always load question state (flag and hide) when question changes
    await this.loadQuestionState();

    // Load data based on active tab
    switch (this.activeTab) {
      case 'explanation':
        await this.loadExplanation();
        break;
      case 'note':
        await this.loadNotes();
        break;
      case 'exam':
        await this.loadExamState();
        break;
      case 'comments':
        await this.loadComments();
        break;
    }
  }

  async loadQuestionState() {
    this.isLoadingState = true;
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (sessionId) {
        const state = await this.restService.getQuestionState(sessionId, this.questionId);
        if (state) {
          this.currentFlagId = state.flagId;
          this.currentHide = state.hide || 0;
        } else {
          this.currentFlagId = null;
          this.currentHide = 0;
        }
      }
    } catch (error) {
      LoggerService.error('Error loading question state:', error);
    } finally {
      this.isLoadingState = false;
    }
  }

  async setFlag(flagId: number | null) {
    LoggerService.log(`setFlag called with flagId=${flagId}, currentFlagId=${this.currentFlagId}`);
    // Toggle: if clicking the same flag, unset it
    const newFlagId = this.currentFlagId === flagId ? null : flagId;
    LoggerService.log(`setFlag: newFlagId=${newFlagId}, currentHide=${this.currentHide}`);
    await this.saveQuestionState(newFlagId, this.currentHide);
  }

  async toggleHide() {
    LoggerService.log(`toggleHide called, currentHide=${this.currentHide}`);
    // Toggle hide state
    const newHide = this.currentHide === 1 ? 0 : 1;
    LoggerService.log(`toggleHide: newHide=${newHide}, currentFlagId=${this.currentFlagId}`);
    await this.saveQuestionState(this.currentFlagId, newHide);
  }

  async saveQuestionState(flagId: number | null, hide: number) {
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (sessionId) {
        LoggerService.log(`Saving question state: questionId=${this.questionId}, flagId=${flagId}, hide=${hide}`);
        const success = await this.restService.saveQuestionState(sessionId, this.questionId, flagId, hide);
        if (success) {
          this.currentFlagId = flagId;
          this.currentHide = hide;
          LoggerService.log(`Question state saved successfully: flagId=${flagId}, hide=${hide}`);
        } else {
          LoggerService.error('Failed to save question state', null);
        }
      } else {
        LoggerService.error('No session ID available', null);
      }
    } catch (error) {
      LoggerService.error('Error saving question state:', error);
    }
  }

  async loadExplanation() {
    this.isLoadingExplanation = true;
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (sessionId) {
        this.explanationData = await this.restService.getQuestionExplanation(sessionId, this.questionId);
      }
    } catch (error) {
      LoggerService.error('Error loading explanation:', error);
    } finally {
      this.isLoadingExplanation = false;
    }
  }

  async loadNotes() {
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (sessionId) {
        const notes = await this.restService.getQuestionNotes(sessionId, this.questionId);
        this.questionNotes = notes || '';
        this.savedNotes = notes || '';
        this.hasNote = !!(notes && notes.trim().length > 0);
      }
    } catch (error) {
      LoggerService.error('Error loading notes:', error);
    }
  }

  onNotesInput() {
    // Clear any pending save timeout
    if (this.notesSaveTimeout) {
      clearTimeout(this.notesSaveTimeout);
    }
    // Update hasNote status
    this.hasNote = !!(this.questionNotes && this.questionNotes.trim().length > 0);
  }

  async autoSaveNotes() {
    // Only save if notes have changed
    if (this.questionNotes === this.savedNotes) {
      return;
    }

    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (sessionId) {
        const success = await this.restService.saveQuestionNotes(sessionId, this.questionId, this.questionNotes);
        if (success) {
          this.savedNotes = this.questionNotes;
          this.hasNote = !!(this.questionNotes && this.questionNotes.trim().length > 0);
        }
      }
    } catch (error) {
      LoggerService.error('Error auto-saving notes:', error);
    }
  }

  async loadExamState() {
    this.isLoadingExamState = true;
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (sessionId) {
        this.examStateData = await this.restService.getQuestionExamState(sessionId, this.questionId) || [];
      }
    } catch (error) {
      LoggerService.error('Error loading exam state:', error);
    } finally {
      this.isLoadingExamState = false;
    }
  }

  async loadComments() {
    this.isLoadingComments = true;
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (sessionId) {
        this.commentsData = await this.restService.getQuestionComments(sessionId, this.questionId) || [];
        // Get current user ID for validation - try to get from auth service user object
        // The user object might have an id field, or we can get it from the profile
        const userProfile = await this.restService.getUserProfile(sessionId);
        if (userProfile && userProfile.id) {
          this.currentUserId = userProfile.id;
        }
        // Check if user can submit comment (last comment must not be from same user)
        this.updateCanSubmitComment();
      }
    } catch (error) {
      LoggerService.error('Error loading comments:', error);
    } finally {
      this.isLoadingComments = false;
    }
  }

  updateCanSubmitComment() {
    if (!this.commentsData || this.commentsData.length === 0) {
      // No comments yet, user can submit
      this.canSubmitComment = true;
      return;
    }
    // Check if the last comment (first in array since sorted descending) is from current user
    const lastComment = this.commentsData[0];
    this.canSubmitComment = !(this.currentUserId && lastComment.userId === this.currentUserId);
  }

  submitComment() {
    if (!this.newComment || this.newComment.trim().length === 0) {
      return;
    }
    // Show confirmation dialog
    this.showCommentConfirm = true;
  }

  cancelComment() {
    this.showCommentConfirm = false;
  }

  async confirmComment() {
    this.showCommentConfirm = false;
    this.isSubmittingComment = true;
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (sessionId) {
        try {
          const success = await this.restService.submitQuestionComment(sessionId, this.questionId, this.newComment.trim());
          if (success) {
            // Clear the comment field
            this.newComment = '';
            // Reload comments to show the new one
            await this.loadComments();
          }
        } catch (error: any) {
          // Handle validation error from backend
          if (error.message) {
            LoggerService.error('Comment submission error:', error.message);
            // The error message will be shown via canSubmitComment being false
            await this.loadComments(); // Reload to update canSubmitComment
          } else {
            LoggerService.error('Failed to submit comment', null);
          }
        }
      }
    } catch (error) {
      LoggerService.error('Error submitting comment:', error);
    } finally {
      this.isSubmittingComment = false;
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (e) {
      return dateString;
    }
  }

  // reacts on keyRight and moves to next question
  nextQuestion(event?:any) {
    // Prevent rapid navigation using static variable
    if (QuestionPoolStudyComponent.isNavigating) {
      console.log('[DEBUG nextQuestion] Already navigating, skipping');
      return false;
    }
    
    // CRITICAL: Use component.questionId instead of route snapshot
    // The route snapshot can be stale during navigation, but component.questionId
    // is updated via the setter when route params change
    const currentQuestionId = this.questionId;
    
    // Check if we're trying to navigate to the same question we just navigated to
    if (QuestionPoolStudyComponent.lastNavigatedQuestionId === currentQuestionId) {
      console.log('[DEBUG nextQuestion] Already navigated to this question, waiting for route update');
      return false;
    }
    
    console.log('[DEBUG nextQuestion] START - current questionId:', currentQuestionId);
    // Save notes before moving to next question
    this.autoSaveNotes();
    let questionIds = this.restService.getQuestionIds();
    console.log('[DEBUG nextQuestion] questionIds:', questionIds, 'length:', questionIds.length);
    console.log('[DEBUG nextQuestion] Last questionId:', questionIds[questionIds.length-1]);
    console.log('[DEBUG nextQuestion] Current questionId type:', typeof currentQuestionId, 'value:', currentQuestionId);
    console.log('[DEBUG nextQuestion] First few questionIds:', questionIds.slice(0, 5), 'types:', questionIds.slice(0, 5).map(id => typeof id));
    
    if (currentQuestionId && currentQuestionId != questionIds[questionIds.length-1]) {
      // Use strict comparison and ensure types match
      const currentIndex = questionIds.findIndex(id => Number(id) === Number(currentQuestionId));
      console.log('[DEBUG nextQuestion] Current index:', currentIndex, 'looking for questionId:', currentQuestionId);
      
      if (currentIndex === -1) {
        console.error('[DEBUG nextQuestion] ERROR: Current questionId', currentQuestionId, 'not found in questionIds array!');
        console.error('[DEBUG nextQuestion] questionIds array:', questionIds);
        console.error('[DEBUG nextQuestion] Checking if questionId exists with different type...');
        const stringIndex = questionIds.findIndex(id => String(id) === String(currentQuestionId));
        console.error('[DEBUG nextQuestion] Found at index (as string):', stringIndex);
        return false;
      }
      
      if (currentIndex >= 0 && currentIndex < questionIds.length - 1) {
        const nextId = questionIds[currentIndex + 1];
        console.log('[DEBUG nextQuestion] Found current at index', currentIndex, 'navigating to nextId:', nextId, 'at index', currentIndex + 1);
        console.log('[DEBUG nextQuestion] Next few IDs after current:', questionIds.slice(currentIndex, currentIndex + 3));
        QuestionPoolStudyComponent.isNavigating = true;
        QuestionPoolStudyComponent.lastNavigatedQuestionId = nextId;
        this.router.navigate(['/question-pool-study/', ''+nextId], { replaceUrl: true }).then(() => {
          console.log('[DEBUG nextQuestion] Navigation complete, resetting flag');
          setTimeout(() => { 
            QuestionPoolStudyComponent.isNavigating = false;
            // Reset lastNavigatedQuestionId after a delay to allow route to update
            setTimeout(() => {
              QuestionPoolStudyComponent.lastNavigatedQuestionId = null;
            }, 200);
          }, 100);
        }).catch((err) => {
          console.error('[DEBUG nextQuestion] Navigation error:', err);
          QuestionPoolStudyComponent.isNavigating = false;
          QuestionPoolStudyComponent.lastNavigatedQuestionId = null;
        });
      } else {
        console.log('[DEBUG nextQuestion] Current question not found in list or already at last');
      }
    } else {
      console.log('[DEBUG nextQuestion] Already at last question or invalid questionId, not navigating');
    }
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    return false;
  }

  // reacts on keyLeft and moves to previous question
  prevQuestion(event?:any) {
    // Prevent rapid navigation using static variable
    if (QuestionPoolStudyComponent.isNavigating) {
      console.log('[DEBUG prevQuestion] Already navigating, skipping');
      return false;
    }
    
    // CRITICAL: Use component.questionId instead of route snapshot
    // The route snapshot can be stale during navigation, but component.questionId
    // is updated via the setter when route params change
    const currentQuestionId = this.questionId;
    
    // Check if we're trying to navigate to the same question we just navigated to
    if (QuestionPoolStudyComponent.lastNavigatedQuestionId === currentQuestionId) {
      console.log('[DEBUG prevQuestion] Already navigated to this question, waiting for route update');
      return false;
    }
    
    console.log('[DEBUG prevQuestion] START - current questionId:', currentQuestionId);
    // Save notes before moving to previous question
    this.autoSaveNotes();
    let questionIds = this.restService.getQuestionIds();
    console.log('[DEBUG prevQuestion] questionIds:', questionIds);
    console.log('[DEBUG prevQuestion] Current questionId type:', typeof currentQuestionId, 'value:', currentQuestionId);
    
    if (currentQuestionId) {
      // Use strict comparison and ensure types match
      const currentIndex = questionIds.findIndex(id => Number(id) === Number(currentQuestionId));
      console.log('[DEBUG prevQuestion] Current index:', currentIndex, 'looking for questionId:', currentQuestionId);
      
      if (currentIndex === -1) {
        console.error('[DEBUG prevQuestion] ERROR: Current questionId', currentQuestionId, 'not found in questionIds array!');
        console.error('[DEBUG prevQuestion] questionIds array:', questionIds);
        return false;
      }
      
      if (currentIndex > 0) {
        const prevId = questionIds[currentIndex - 1];
        console.log('[DEBUG prevQuestion] Found current at index', currentIndex, 'navigating to prevId:', prevId, 'at index', currentIndex - 1);
        console.log('[DEBUG prevQuestion] Previous few IDs before current:', questionIds.slice(Math.max(0, currentIndex - 2), currentIndex + 1));
        QuestionPoolStudyComponent.isNavigating = true;
        QuestionPoolStudyComponent.lastNavigatedQuestionId = prevId;
        this.router.navigate(['/question-pool-study/', ''+prevId], { replaceUrl: true }).then(() => {
          console.log('[DEBUG prevQuestion] Navigation complete, resetting flag');
          setTimeout(() => { 
            QuestionPoolStudyComponent.isNavigating = false;
            // Reset lastNavigatedQuestionId after a delay to allow route to update
            setTimeout(() => {
              QuestionPoolStudyComponent.lastNavigatedQuestionId = null;
            }, 200);
          }, 100);
        }).catch((err) => {
          console.error('[DEBUG prevQuestion] Navigation error:', err);
          QuestionPoolStudyComponent.isNavigating = false;
          QuestionPoolStudyComponent.lastNavigatedQuestionId = null;
        });
      } else {
        console.log('[DEBUG prevQuestion] Already at first question or current not found');
      }
    }
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    return false;
  }

  // reacts on click and focus on container to allow arrows work
  protected containerClick(event: any){
    // Don't steal focus if clicking on input/textarea elements
    const target = event.target;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'BUTTON')) {
      return;
    }
    this.container?.nativeElement.focus();
  }

  protected async finishStudy(){
    // Save notes before finishing
    this.autoSaveNotes();
    // Wait for pending answer saves so the report loads with up-to-date backend data
    await this.restService.flushAnswerQueue();
    this.goToResults();
  }

  /** Confirm-before-finish — used by both the desktop "Finish Test" button
   * and the mobile sticky bar's green check. Only fires when at least one
   * question is unanswered; a fully-answered test finishes silently. */
  protected showFinishConfirm = false;
  protected finishUnansweredCount = 0;

  protected requestFinish(): void {
    const unanswered = this.getUnansweredCount();
    if (unanswered === 0) {
      void this.finishStudy();
      return;
    }
    this.finishUnansweredCount = unanswered;
    this.showFinishConfirm = true;
  }

  protected cancelFinish(): void {
    this.showFinishConfirm = false;
  }

  protected async confirmFinish(): Promise<void> {
    this.showFinishConfirm = false;
    await this.finishStudy();
  }

  /** Counts questions in the current test that haven't been answered yet.
   * Pulls the freshest map from `restService` because answers may have just
   * been saved in the background. */
  protected getUnansweredCount(): number {
    this.questionsMap = this.restService.getQuestionsMap();
    const questionIds = this.restService.getQuestionIds();
    let unanswered = 0;
    for (const id of questionIds) {
      const q = this.questionsMap[id];
      if (!q || q.answered !== true) unanswered++;
    }
    return unanswered;
  }

  /** Mobile users want a confirm on Cancel even with zero answers (thumb
   * mis-tap on the red X is too easy). Desktop keeps the silent-on-empty
   * behavior the web has always had. */
  protected requestCancelFromMobile(): void {
    this.autoSaveNotes();
    if (this.hasAnsweredQuestions()) {
      this.showCancelConfirm = true;
    } else {
      // Reuse the same modal — cancelWithoutSaving is the only sensible action
      // when nothing has been answered, but we still ask first.
      this.showCancelConfirm = true;
    }
  }

  protected showCancelConfirm = false;

  /** Shown when user tries to leave the page (link, back, etc.) */
  protected showLeaveConfirm = false;
  private pendingLeaveSubject: Subject<boolean> | null = null;
  /** When user confirms Leave, we navigate here with replaceUrl so test is not in history. */
  private pendingLeaveNextUrl: string | null = null;
  /** Set when navigating away after Cancel (without saving / to resume later); skip Leave dialog. */
  private navigatingAfterCancel = false;

  canDeactivate(_currentRoute?: unknown, _currentState?: unknown, nextState?: RouterStateSnapshot): Observable<boolean> | boolean {
    if (this.showCancelConfirm) return false;
    if (this.navigatingAfterCancel) return true;
    const questionIds = this.restService.getQuestionIds();
    if (!questionIds.length) return true;
    if (!this.hasAnsweredQuestions()) return true;
    const nextUrl = nextState?.url ?? '';
    // Navigating to another question in the same study test – allow without prompting
    if (nextUrl.includes('/question-pool-study/')) return true;
    // Navigating to report (e.g. after Finish Test + Leave) – allow without prompting
    if (nextUrl.includes('/question-pool-results/')) return true;
    this.showLeaveConfirm = true;
    this.pendingLeaveNextUrl = (nextState?.url != null ? String(nextState.url) : null);
    this.pendingLeaveSubject = new Subject<boolean>();
    this.cdr.detectChanges();
    setTimeout(() => this.leaveModalStayBtn?.nativeElement?.focus(), 0);
    return this.pendingLeaveSubject.pipe(take(1));
  }

  protected confirmLeavePage(leave: boolean): void {
    this.showLeaveConfirm = false;
    if (leave) {
      this.navigatingAfterCancel = true;
      const nextUrl = this.pendingLeaveNextUrl;
      void this.deleteTestOnly().then(() => {
        if (nextUrl) {
          this.router.navigateByUrl(nextUrl, { replaceUrl: true });
        }
        if (this.pendingLeaveSubject) {
          this.pendingLeaveSubject.next(false);
          this.pendingLeaveSubject.complete();
          this.pendingLeaveSubject = null;
        }
        this.pendingLeaveNextUrl = null;
      });
      return;
    }
    if (this.pendingLeaveSubject) {
      this.pendingLeaveSubject.next(leave);
      this.pendingLeaveSubject.complete();
      this.pendingLeaveSubject = null;
    }
    this.pendingLeaveNextUrl = null;
  }

  protected cancelTest(){
    // Save notes before cancelling
    this.autoSaveNotes();
    // Check if any questions were answered
    if (this.hasAnsweredQuestions()) {
      this.showCancelConfirm = true;
    } else {
      this.navigatingAfterCancel = true;
      this.deleteTestAndNavigate();
    }
  }

  protected continueTest(){
    this.showCancelConfirm = false;
  }

  protected cancelWithoutSaving(){
    this.showCancelConfirm = false;
    this.navigatingAfterCancel = true;
    this.deleteTestAndNavigate();
  }

  protected cancelToResumeLater(){
    this.autoSaveNotes();
    this.showCancelConfirm = false;
    this.navigatingAfterCancel = true;
    this.router.navigate(['/question-pool'], { replaceUrl: true });
  }

  /** Hard-delete current test only (no navigation). Used when user chooses "Leave" from leave-confirm dialog. */
  private async deleteTestOnly(): Promise<void> {
    const user = this.authService.getUser();
    const sessionId = user?.sid;
    const userTestId = this.restService.currentUserTestId;
    if (sessionId && userTestId) {
      try {
        const success = await this.restService.hardDeleteUserTest(sessionId, userTestId);
        if (success) {
          this.restService.currentUserTestId = null;
          LoggerService.log(`[leavePage] Hard deleted user test ${userTestId}`);
        } else {
          LoggerService.error('Failed to hard delete user test', null);
        }
      } catch (error) {
        LoggerService.error('Error hard deleting user test:', error);
      }
    }
  }

  private async deleteTestAndNavigate(){
    await this.deleteTestOnly();
    this.router.navigate(['/question-pool'], { replaceUrl: true });
  }

  protected hasAnsweredQuestions(): boolean {
    // Update questionsMap from restService to get latest state
    this.questionsMap = this.restService.getQuestionsMap();
    const questionIds = this.restService.getQuestionIds();
    for (const id of questionIds) {
      const question = this.questionsMap[id];
      if (question && question.answered) {
        return true;
      }
    }
    return false;
  }

  private goToResults(): void {
    // Results page expects the userTestId, not the current questionId.
    // replaceUrl so browser back from report goes to New Test, not to the test.
    const userTestId = this.restService.currentUserTestId;
    if (userTestId) {
      this.router.navigate(
        ['/question-pool-results/', userTestId],
        { replaceUrl: true, state: { skyTransition: true } },
      );
    } else {
      console.error('[goToResults] No userTestId available, cannot navigate to results');
      this.router.navigate(['/question-pool'], { replaceUrl: true });
    }
  }

  showShortcuts() {
    this.showShortcutsModal = true;
  }

  openImageOverlay(url: string) {
    this.imageOverlayUrl = url;
  }

  closeImageOverlay() {
    this.imageOverlayUrl = null;
  }

  closeShortcuts() {
    this.showShortcutsModal = false;
  }

  openFlightComputer(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.flightComputerOpenTimer != null) {
      clearTimeout(this.flightComputerOpenTimer);
      this.flightComputerOpenTimer = null;
    }
    // Defer mount so the same click/tap cannot hit the new backdrop and close it.
    this.flightComputerOpenTimer = setTimeout(() => {
      this.flightComputerOpenTimer = null;
      this.flightComputerOpenedAt = Date.now();
      this.flightComputerUiMode = 'interactive';
      this.flightComputerInstructions = null;
      this.showFlightComputer = true;
      this.cdr.detectChanges();
    }, 0);
  }

  openE6bPlayer() {
    const instructions = this.explanationData?.e6bInstructions;
    if (!Array.isArray(instructions) || !instructions.length) return;
    if (this.flightComputerOpenTimer != null) {
      clearTimeout(this.flightComputerOpenTimer);
      this.flightComputerOpenTimer = null;
    }
    this.flightComputerOpenedAt = Date.now();
    this.flightComputerUiMode = 'playback';
    this.flightComputerInstructions = instructions;
    this.showFlightComputer = true;
    this.cdr.detectChanges();
  }

  closeFlightComputer() {
    if (this.showFlightComputer && Date.now() - this.flightComputerOpenedAt < 400) {
      return;
    }
    if (this.flightComputerOpenTimer != null) {
      clearTimeout(this.flightComputerOpenTimer);
      this.flightComputerOpenTimer = null;
    }
    this.showFlightComputer = false;
    this.flightComputerUiMode = 'interactive';
    this.flightComputerInstructions = null;
  }

  // kept name used by older call sites / keyboard close paths
  closeE6bPlayer() {
    this.forceCloseFlightComputer();
  }

  private forceCloseFlightComputer() {
    if (this.flightComputerOpenTimer != null) {
      clearTimeout(this.flightComputerOpenTimer);
      this.flightComputerOpenTimer = null;
    }
    this.showFlightComputer = false;
    this.flightComputerUiMode = 'interactive';
    this.flightComputerInstructions = null;
  }

  /** Whether the question grid should be paginated (> 50 questions) */
  protected get usePaginatedGrid(): boolean {
    return this.restService.getQuestionIds().length > this.QUESTIONS_PER_PAGE;
  }

  /** Question IDs to show on the current page (paginated) or all (non-paginated) */
  protected getVisibleQuestionIds(): number[] {
    const ids = this.restService.getQuestionIds();
    if (ids.length <= this.QUESTIONS_PER_PAGE) {
      return ids;
    }
    const start = (this.questionGridPage - 1) * this.QUESTIONS_PER_PAGE;
    return ids.slice(start, start + this.QUESTIONS_PER_PAGE);
  }

  /** 1-based question number for display (e.g. question 53 of 100) */
  protected getQuestionNumber(questionId: number): number {
    const ids = this.restService.getQuestionIds();
    const idx = ids.findIndex(id => Number(id) === Number(questionId));
    return idx >= 0 ? idx + 1 : 0;
  }

  /** Total number of pages for the question grid */
  protected getTotalPages(): number {
    const ids = this.restService.getQuestionIds();
    return Math.max(1, Math.ceil(ids.length / this.QUESTIONS_PER_PAGE));
  }

  protected getTotalQuestions(): number {
    return this.restService.getQuestionIds().length;
  }

  protected toggleGridSheet(): void {
    this.gridSheetOpen = !this.gridSheetOpen;
  }

  protected closeGridSheet(): void {
    this.gridSheetOpen = false;
  }

  /** Page numbers to show in pagination (e.g. [1,2,3,4,5]) */
  protected getPageNumbers(): number[] {
    const total = this.getTotalPages();
    const pages: number[] = [];
    const maxVisible = 5;
    let start = Math.max(1, this.questionGridPage - Math.floor(maxVisible / 2));
    const end = Math.min(total, start + maxVisible - 1);
    start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  protected goToPage(page: number) {
    const total = this.getTotalPages();
    if (page >= 1 && page <= total) {
      this.questionGridPage = page;
    }
  }

  /** Ensure the current grid page contains the given question (when paginated) */
  private ensurePageContainsQuestion(questionId: number) {
    if (!this.usePaginatedGrid) return;
    const ids = this.restService.getQuestionIds();
    const index = ids.findIndex(id => Number(id) === Number(questionId));
    if (index >= 0) {
      const page = Math.floor(index / this.QUESTIONS_PER_PAGE) + 1;
      if (page !== this.questionGridPage) {
        this.questionGridPage = page;
      }
    }
  }

  private handleKeyboardShortcuts(event: KeyboardEvent) {
    // Only log/handle relevant keys (keeps noise down, but still high-signal for debugging)
    const isRelevantKey =
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight' ||
      event.key === 'e' ||
      event.key === 'E' ||
      /^[1-4]$/.test(event.key);
    if (!isRelevantKey) return;

    const seq = ++QuestionPoolStudyComponent.keyboardSeq;

    // Use the current component instance from static reference
    const component = QuestionPoolStudyComponent.keyboardHandlerInstance;
    if (!component) {
      console.log(`[KB-STUDY ${seq}] ignored: no component instance`, { key: event.key, code: (event as any).code });
      return;
    }
    
    // Check if we're still on a study route before handling
    const currentUrl = component.router.url;
    if (!currentUrl.includes('/question-pool-study/')) {
      console.log(`[KB-STUDY ${seq}] ignored: not on study route`, { key: event.key, url: currentUrl });
      return;
    }
    
    // CRITICAL: Use component.questionId instead of route snapshot
    // The route snapshot can be stale during navigation, but component.questionId
    // is updated via the setter when route params change, ensuring we have the correct current question
    const currentQuestionId = component.questionId;
    
    if (!currentQuestionId) {
      console.log(`[KB-STUDY ${seq}] ignored: no questionId`, { key: event.key, url: currentUrl });
      return;
    }
    
    // CRITICAL: Check if component is still initializing (questionDetail might not be ready yet)
    // Wait a bit if questionDetail is not available or question is not loaded
    if (!component.questionDetail || !component.questionDetail.question) {
      console.log(`[KB-STUDY ${seq}] ignored: questionDetail not ready`, {
        key: event.key,
        url: currentUrl,
        questionId: currentQuestionId,
        hasQuestionDetail: !!component.questionDetail,
        hasQuestion: !!component.questionDetail?.question,
      });
      return;
    }
    
    // CRITICAL: Verify the loaded question matches the current component questionId
    // During navigation, the questionDetail might still show the old question
    const loadedQuestionId = component.questionDetail.question.id;
    if (loadedQuestionId !== currentQuestionId) {
      console.log(`[KB-STUDY ${seq}] ignored: question mismatch`, {
        key: event.key,
        url: currentUrl,
        questionId: currentQuestionId,
        loadedQuestionId,
      });
      return;
    }

    const target = event.target as HTMLElement | null;
    const activeEl = (typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null);
    const targetTag = target?.tagName?.toUpperCase();
    const activeTag = activeEl?.tagName?.toUpperCase();
    const activeId = activeEl?.id;
    const activeClass = activeEl?.className;

    // We only want to suppress shortcuts while the user is typing text.
    // Previously, any INPUT blocked shortcuts; that also blocked when focus was on answer checkboxes.
    const isTextEntryTarget = (() => {
      if (!target) return false;
      // Anything inside contenteditable
      if ((target as any).isContentEditable) return true;
      // Textareas always mean typing
      if (targetTag === 'TEXTAREA') return true;
      // Inputs: only block typical text-entry inputs, NOT checkboxes
      if (targetTag === 'INPUT') {
        const input = target as HTMLInputElement;
        const type = (input.type || '').toLowerCase();
        return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'file', 'color', 'date', 'time'].includes(type);
      }
      return false;
    })();

    console.log(`[KB-STUDY ${seq}] keydown`, {
      key: event.key,
      code: (event as any).code,
      repeat: event.repeat,
      url: currentUrl,
      questionId: currentQuestionId,
      targetTag,
      activeTag,
      activeId,
      activeClass,
      isTextEntryTarget,
    });
    
    // Don't handle shortcuts if modals are open or when typing in input fields
    if (component.showShortcutsModal || component.showCancelConfirm || component.showLeaveConfirm || component.showCommentConfirm || component.showFinishConfirm || component.imageOverlayUrl || component.showFlightComputer) {
      console.log(`[KB-STUDY ${seq}] ignored: modal open`, {
        showShortcutsModal: component.showShortcutsModal,
        showCancelConfirm: component.showCancelConfirm,
        showLeaveConfirm: component.showLeaveConfirm,
        showCommentConfirm: component.showCommentConfirm,
        showFinishConfirm: component.showFinishConfirm,
        showFlightComputer: component.showFlightComputer,
      });
      return;
    }
    if (isTextEntryTarget) {
      console.log(`[KB-STUDY ${seq}] ignored: typing in text field`);
      return;
    }

    // Arrow keys for navigation
    if (event.key === 'ArrowLeft') {
      console.log(`[KB-STUDY ${seq}] action: ArrowLeft -> prevQuestion`);
      event.preventDefault();
      event.stopPropagation();
      component.prevQuestion(event);
      return;
    } else if (event.key === 'ArrowRight') {
      console.log(`[KB-STUDY ${seq}] action: ArrowRight -> nextQuestion`);
      event.preventDefault();
      event.stopPropagation();
      component.nextQuestion(event);
      return;
    }

    // E opens the flight computer
    if (event.key === 'e' || event.key === 'E') {
      console.log(`[KB-STUDY ${seq}] action: E -> openFlightComputer`);
      event.preventDefault();
      event.stopPropagation();
      component.openFlightComputer(event);
      return;
    }

    // Number keys (1-4) for answer selection
    if (/^[1-4]$/.test(event.key)) {
      console.log(`[KB-STUDY ${seq}] action: digit`, { digit: event.key });
      
      // At this point we've already verified loadedQuestionId === currentQuestionId above
      // Check if question is already answered
      if (component.questionDetail && component.questionDetail.question && component.questionDetail.question.answered) {
        console.log(`[KB-STUDY ${seq}] ignored: question already answered`);
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      
      event.preventDefault();
      event.stopPropagation();
      const answerIndex = parseInt(event.key) - 1; // 1 -> 0, 2 -> 1, etc.
      
      console.log(`[KB-STUDY ${seq}] selecting answerIndex`, { answerIndex });
      
      // Access the question detail component's form
      if (component.questionDetail && component.questionDetail.question && answerIndex < component.questionDetail.question.answers.length) {
        const formArray = component.questionDetail.questionDetailForm.get('answers') as FormArray;
        const control = formArray.at(answerIndex);
        console.log(`[KB-STUDY ${seq}] form control`, {
          hasFormArray: !!formArray,
          hasControl: !!control,
          disabled: (control as any)?.disabled,
          currentValue: (control as any)?.value,
        });
        if (control && !control.disabled) {
          // Toggle the checkbox
          const newValue = !control.value;
          control.setValue(newValue);
          console.log(`[KB-STUDY ${seq}] setValue`, { newValue });
          // Trigger change event - need to match the format onChange expects
          const answerOption = `answer_${answerIndex}`;
          const changeEvent = { 
            target: { 
              checked: newValue, 
              id: answerOption 
            } 
          } as any;
          console.log(`[KB-STUDY ${seq}] calling questionDetail.onChange`, { id: answerOption, checked: newValue });
          component.questionDetail.onChange(changeEvent);
          console.log(`[KB-STUDY ${seq}] onChange returned`, {
            answered: component.questionDetail.question?.answered,
            correctAnswer: component.questionDetail.question?.correctAnswer,
          });
          
          // CRITICAL: Trigger change detection to update button colors
          // The onChange sets isSubmitted and question.answered, but Angular might not detect the change immediately
          // Use setTimeout to ensure change detection runs after onChange completes
          setTimeout(() => {
            if (component.cdr) {
              component.cdr.detectChanges();
            }
          }, 0);
        } else {
          console.log(`[KB-STUDY ${seq}] ignored: control missing/disabled`);
        }
      } else {
        console.log(`[KB-STUDY ${seq}] ignored: answerIndex out of range`, {
          hasQuestionDetail: !!component.questionDetail,
          hasQuestion: !!component.questionDetail?.question,
          answersLength: component.questionDetail?.question?.answers?.length,
          answerIndex,
        });
      }
    }
  }
}
